import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { API } from "@/lib/api";
import { money, dt } from "@/lib/format";
import { PageHeader, Loader, EmptyState } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Scale, Download, AlertTriangle, Lock, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

function firstOfMonth() {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
}
function today() { return new Date().toISOString().slice(0, 10); }

export default function Reconciliation() {
  const nav = useNavigate();
  const [desde, setDesde] = useState(firstOfMonth());
  const [hasta, setHasta] = useState(today());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fechaCierre, setFechaCierre] = useState(today());
  const [cierres, setCierres] = useState([]);
  const [confirmCierre, setConfirmCierre] = useState(false);
  const [closing, setClosing] = useState(false);
  const [verCierre, setVerCierre] = useState(null);

  const calcular = async () => {
    setLoading(true);
    try {
      const r = await api.get("/conciliacion", { params: { desde, hasta } });
      setData(r.data);
    } catch { toast.error("No se pudo calcular"); }
    finally { setLoading(false); }
  };
  const loadCierres = async () => { const r = await api.get("/conciliacion/cierres"); setCierres(r.data); };
  useEffect(() => { loadCierres(); }, []);

  const exportCsv = () => {
    const token = localStorage.getItem("ms_token");
    window.open(`${API}/conciliacion/export?desde=${desde}&hasta=${hasta}&auth=${token}`, "_blank");
  };

  const goToException = (e) => {
    if (e.referencia_tipo === "pedido") nav(`/pedidos?abrir=${e.referencia_id}`);
    else if (e.referencia_tipo === "pago") nav(`/pagos?abrir=${e.referencia_id}`);
    else if (e.referencia_tipo === "mensajero") nav("/rendiciones");
    else nav("/productos");
  };

  const cerrarDia = async () => {
    setClosing(true);
    try {
      await api.post("/conciliacion/cerrar", null, { params: { fecha: fechaCierre } });
      toast.success(`Día ${fechaCierre} cerrado`);
      setConfirmCierre(false);
      loadCierres();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setClosing(false); }
  };

  return (
    <div className="p-4 sm:p-8 max-w-4xl mx-auto">
      <PageHeader title="Conciliación" subtitle="Cruce de ventas, cobros e inventario, y cierre del día." />

      <Tabs defaultValue="resumen">
        <TabsList className="rounded-full mb-4">
          <TabsTrigger value="resumen" className="rounded-full" data-testid="tab-conciliacion-resumen">Resumen</TabsTrigger>
          <TabsTrigger value="cierres" className="rounded-full" data-testid="tab-conciliacion-cierres">Cierres ({cierres.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="resumen">
          <div className="ms-card p-4 mb-4 flex flex-wrap items-end gap-3">
            <div><Label>Desde</Label><Input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} className="rounded-xl mt-1" data-testid="conciliacion-desde" /></div>
            <div><Label>Hasta</Label><Input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} className="rounded-xl mt-1" data-testid="conciliacion-hasta" /></div>
            <Button onClick={calcular} disabled={loading} className="rounded-full bg-primary hover:bg-primary/90" data-testid="conciliacion-calcular">
              {loading ? "Calculando…" : "Calcular"}
            </Button>
            {data && (
              <Button variant="outline" className="rounded-full gap-2" onClick={exportCsv} data-testid="conciliacion-export"><Download className="h-4 w-4" /> Exportar CSV</Button>
            )}
          </div>

          {loading ? <Loader /> : !data ? (
            <EmptyState icon={Scale} title="Elegí un rango" subtitle="Calculá para ver el resumen y las excepciones." />
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
                <div className="ms-card p-4"><p className="text-xs text-muted-foreground">Total vendido</p><p className="font-head text-xl font-semibold mt-1">{money(data.resumen.total_vendido)}</p></div>
                <div className="ms-card p-4"><p className="text-xs text-muted-foreground">Total cobrado validado</p><p className="font-head text-xl font-semibold mt-1 text-emerald-600">{money(data.resumen.total_cobrado)}</p></div>
                <div className="ms-card p-4"><p className="text-xs text-muted-foreground">Diferencia</p>
                  <p className={`font-head text-xl font-semibold mt-1 ${Math.abs(data.resumen.diferencia) > 0.5 ? "text-red-500" : "text-emerald-600"}`} data-testid="conciliacion-diferencia">{money(data.resumen.diferencia)}</p>
                </div>
              </div>

              <div className="ms-card p-4 mb-4">
                <h3 className="font-head font-semibold text-sm mb-2">Cobrado por medio de pago</h3>
                {Object.keys(data.resumen.por_medio_pago).length === 0 ? <p className="text-sm text-muted-foreground">Sin pagos validados en el rango.</p> : (
                  <div className="flex flex-wrap gap-3">
                    {Object.entries(data.resumen.por_medio_pago).map(([k, v]) => (
                      <div key={k} className="rounded-xl bg-accent px-3 py-2 text-sm"><span className="capitalize">{k.replace("_", " ")}</span>: <strong>{money(v)}</strong></div>
                    ))}
                  </div>
                )}
                <p className="text-xs text-muted-foreground mt-3">Unidades vendidas (pedidos): {data.resumen.unidades_vendidas_pedidos} · Unidades por movimientos de salida: {data.resumen.unidades_movimientos_salida} · Rendiciones abiertas: {data.resumen.rendiciones_abiertas}</p>
              </div>

              {data.ajustes_stock.length > 0 && (
                <div className="ms-card p-4 mb-4">
                  <h3 className="font-head font-semibold text-sm mb-2">Ajustes de stock del período ({data.ajustes_stock.length})</h3>
                  <div className="space-y-1">
                    {data.ajustes_stock.map((a, i) => (
                      <div key={i} className="text-sm flex justify-between gap-2 border-b border-border/60 pb-1 last:border-0">
                        <span>{a.producto} <span className={a.cantidad < 0 ? "text-red-500" : "text-emerald-600"}>({a.cantidad > 0 ? "+" : ""}{a.cantidad})</span> — {a.motivo}</span>
                        <span className="text-xs text-muted-foreground shrink-0">{a.usuario}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="ms-card p-4 mb-4">
                <h3 className="font-head font-semibold text-sm mb-2">Excepciones ({data.excepciones.length})</h3>
                {data.excepciones.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-2">Sin excepciones en este rango. ✨</p>
                ) : (
                  <div className="space-y-2">
                    {data.excepciones.map((e, i) => (
                      <button key={i} onClick={() => goToException(e)} className="w-full text-left flex items-center gap-3 rounded-xl border border-border px-3 py-2 hover:bg-accent transition-colors" data-testid={`exception-${i}`}>
                        <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0" />
                        <span className="text-sm">{e.descripcion}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="ms-card p-4 flex flex-wrap items-end gap-3">
                <div><Label>Fecha a cerrar</Label><Input type="date" value={fechaCierre} onChange={(e) => setFechaCierre(e.target.value)} className="rounded-xl mt-1" data-testid="conciliacion-fecha-cierre" /></div>
                <Button onClick={() => setConfirmCierre(true)} className="rounded-full bg-primary hover:bg-primary/90 gap-2" data-testid="conciliacion-cerrar-dia-button"><Lock className="h-4 w-4" /> Cerrar el día</Button>
              </div>
            </>
          )}
        </TabsContent>

        <TabsContent value="cierres">
          {cierres.length === 0 ? <EmptyState icon={CheckCircle2} title="Sin cierres todavía" subtitle="Cerrá un día desde la pestaña Resumen." /> : (
            <div className="space-y-2">
              {cierres.map((c) => (
                <button key={c.id} onClick={() => setVerCierre(c)} className="w-full ms-card p-4 flex flex-wrap items-center justify-between gap-3 text-left" data-testid={`cierre-${c.id}`}>
                  <div>
                    <p className="font-semibold text-sm">{c.fecha}</p>
                    <p className="text-xs text-muted-foreground">Cerrado por {c.cerrado_por} · {dt(c.created_at)}</p>
                  </div>
                  <span className={`font-head font-semibold ${Math.abs(c.resumen.diferencia) > 0.5 ? "text-red-500" : "text-emerald-600"}`}>{money(c.resumen.diferencia)}</span>
                </button>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      <Dialog open={confirmCierre} onOpenChange={setConfirmCierre}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Cerrar el {fechaCierre}</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Se guarda una foto inmutable de la conciliación de ese día. No se puede modificar después.</p>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setConfirmCierre(false)}>Cancelar</Button>
            <Button onClick={cerrarDia} disabled={closing} className="rounded-full bg-primary hover:bg-primary/90" data-testid="conciliacion-confirmar-cierre">{closing ? "Cerrando…" : "Sí, cerrar"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!verCierre} onOpenChange={(o) => !o && setVerCierre(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          {verCierre && (
            <>
              <DialogHeader><DialogTitle>Cierre del {verCierre.fecha}</DialogTitle></DialogHeader>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between"><span>Total vendido</span><span>{money(verCierre.resumen.total_vendido)}</span></div>
                <div className="flex justify-between"><span>Total cobrado</span><span>{money(verCierre.resumen.total_cobrado)}</span></div>
                <div className="flex justify-between font-semibold"><span>Diferencia</span><span>{money(verCierre.resumen.diferencia)}</span></div>
                <div className="flex justify-between"><span>Rendiciones abiertas</span><span>{verCierre.resumen.rendiciones_abiertas}</span></div>
                <p className="text-xs text-muted-foreground pt-2">Excepciones registradas al momento del cierre: {verCierre.excepciones.length}</p>
                {verCierre.excepciones.map((e, i) => <p key={i} className="text-xs text-muted-foreground">• {e.descripcion}</p>)}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
